import { useEffect, useMemo } from 'react'
import ReactGA from 'react-ga'
import TagManager from 'react-gtm-module'
import { useLocation } from 'react-router-dom'
import ReactPixel, { Options } from 'react-facebook-pixel'
import { AuthActions, OnboardingWizardActions, DocumentationActions, OnboardingRegisterDataActions } from './actions'

const isProd = process.env.ENVIRONMENT === 'production'
const GTMKey = process.env.PJ_PANEL_GTM_ID
const FacebookPixelKey = process.env.PJ_PANEL_FACEBOOK_PIXEL
const GoogleAnalyticsKey = process.env.PJ_PANEL_GOOGLE_ANALYTICS

const pixelOptions: Options = {
  debug: !isProd,
  autoConfig: true
}

export const initializeTrackers = () => {
  if (GoogleAnalyticsKey) {
    ReactGA.initialize(GoogleAnalyticsKey)
  }
  if (FacebookPixelKey) {
    ReactPixel.init(FacebookPixelKey, undefined, pixelOptions)
  }
  if (GTMKey) {
    TagManager.initialize({ gtmId: GTMKey })
  }
}

export enum EventCategory {
  AUTH_ACTIONS = 'authActions',
  ONBOARDING_WIZARD_ACTIONS = 'cnpjValidation',
  DOCUMENTATION_ACTIONS = 'documentationActions',
  ONBOARDING_REGISTER_DATA_ACTIONS = 'onboardingRegisterDataActions'
}

export interface EventActions {
  [EventCategory.AUTH_ACTIONS]: AuthActions,
  [EventCategory.DOCUMENTATION_ACTIONS]: DocumentationActions,
  [EventCategory.ONBOARDING_WIZARD_ACTIONS]: OnboardingWizardActions,
  [EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS]: OnboardingRegisterDataActions
}

type TrackAction = AuthActions | OnboardingWizardActions | DocumentationActions | OnboardingRegisterDataActions

export const authActions = {
  [AuthActions.LOGIN]: EventCategory.AUTH_ACTIONS,
  [AuthActions.GO_TO_LOGIN]: EventCategory.AUTH_ACTIONS,
  [AuthActions.CREATE_ACCOUNT]: EventCategory.AUTH_ACTIONS,
  [AuthActions.FORGOT_PASSWORD]: EventCategory.AUTH_ACTIONS,
  [AuthActions.RECOVER_PASSWORD]: EventCategory.AUTH_ACTIONS,
  [AuthActions.CREATE_ACCOUNT_ERROR]: EventCategory.AUTH_ACTIONS,
  [AuthActions.SUCCESSFUL_PJ_LOGIN]: EventCategory.AUTH_ACTIONS,
  [AuthActions.UNSUCCESSFUL_PJ_LOGIN]: EventCategory.AUTH_ACTIONS,
  [AuthActions.SUCCESSFUL_RESET_PASSWORD]: EventCategory.AUTH_ACTIONS,
  [AuthActions.UNSUCCESSFUL_RESET_PASSWORD]: EventCategory.AUTH_ACTIONS
}

export const documentationActions = {
  [DocumentationActions.SOLVE_PENDENCY]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_DRE]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_DRE]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_PHOTOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_VIDEOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_PHOTOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_VIDEOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.BEGIN_DOCUMENTATION_STEP]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_ADDITIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_ADDITIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_BALANCE_SHEET]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_BANK_STATEMENT]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_BALANCE_SHEET]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_BANK_STATEMENT]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_SIMPLES_NACIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_DRE]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_SIMPLES_NACIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_PHOTOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_VIDEOS]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_ADDITIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SOLVED_PENDENCY_ARTICLES_OF_INCORPORATION]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_BALANCE_SHEET]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_TAB_ARTICLES_OF_INCORPORATION]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_BANK_STATEMENT]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_SIMPLES_NACIONAL]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.DOCUMENTATION_FINISH_INITIAL_DOCUMENTATION_NEXT_STEP]: EventCategory.DOCUMENTATION_ACTIONS,
  [DocumentationActions.SAVE_BUTTON_DOCUMENTATION_TAB_ARTICLES_OF_INCORPORATION]: EventCategory.DOCUMENTATION_ACTIONS
}

export const onboardActions = {
  [OnboardingWizardActions.VALID_CNPJ]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.DENIED_MEI]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.IS_NEW_CNPJ]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.INVALID_CNPJ]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ACCEPT_TERMS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.INACTIVE_CNPJ]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.DENIED_OTHERS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.CREATED_ACCOUNT]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.INITIAL_ANALYSIS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.SEND_SOLICITATION]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.IS_SMALL_BUSINESS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.VALIDATED_PHONE_CODE]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.VALIDATED_EMAIL_CODE]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.SOLICITATION_SUCCESS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_SEARCH_CNPJ_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.SOLICITATION_SUCCESS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_ADDRESS_NEXT_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_LOAN_DATA_NEXT_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.SEND_SOLICITATION_ERROR]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_ZIPCODE_SEARCH_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_INNER_LOGIN_NEXT_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_12]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_15]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_18]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_21]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.NUMBER_OF_INSTALLMENTS_24]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_COMPANY_DATA_NEXT_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_SOCIAL_MEDIA_NEXT_CLICK]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.RESENT_PHONE_CODE_VALIDATION]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.RESENT_EMAIL_CODE_VALIDATION]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_FIRST_PAGE_NEXT_CLICK_SUCCESS]: EventCategory.ONBOARDING_WIZARD_ACTIONS,
  [OnboardingWizardActions.ON_FIRST_PAGE_NEXT_CLICK_UNSUCCESS]: EventCategory.ONBOARDING_WIZARD_ACTIONS
}

export const onboardingRegisterDataActions = {
  [OnboardingRegisterDataActions.PRE_PROPOSAL_PAGE]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.GO_TO_PENDENCIES_PAGE]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.PJ_PENDENCIES_PAGE]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.FULL_COMERCIAL_ADDRESS]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.FULL_SOCIAL_MEDIA]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.EMAIL_VALIDATION]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.PHONE_VALIDATION]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS,
  [OnboardingRegisterDataActions.GO_TO_DOCUMENTATION_PAGE]: EventCategory.ONBOARDING_REGISTER_DATA_ACTIONS
}

export const trackEvent = (action: TrackAction) => {
  const category = getCategoryFromAction(action)

  ReactPixel.track(action)

  TagManager.dataLayer({ dataLayer: { event: action } })

  ReactGA.event({
    category,
    action
  })
}

const getCategoryFromAction = (action: TrackAction): EventCategory => {
  const categoryMap: { [K in TrackAction]: EventCategory } = {
    ...authActions,
    ...onboardActions,
    ...documentationActions,
    ...onboardingRegisterDataActions
  }

  return categoryMap[action]
}

const isHextString = (value: string) => {
  const hexRegex = /^[0-9a-fA-F]{24}$/

  return hexRegex.test(value)
}

export const usePageTrack = () => {
  const location = useLocation()

  const formatedPathName = useMemo(() => {
    const splitedPathName = location.pathname.split('/').reduce((acc: string[], item) => {
      if (isHextString(item)) {
        return acc
      }

      return [...acc, item]
    }, [])

    return splitedPathName.join('/')
  }, [location.pathname])

  useEffect(() => {
    ReactPixel.pageView()
    ReactGA.pageview(formatedPathName)
  }, [formatedPathName])
}
